import React, { useEffect, useRef, useState } from 'react';
import {KeyboardAvoidingView, Platform, View} from 'react-native';
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';
import { PaperProvider } from 'react-native-paper';
import { NavigationContainer, useNavigationContainerRef } from '@react-navigation/native';
import * as ScreenOrientation from 'expo-screen-orientation';
import * as Notifications from 'expo-notifications';
import ReceiveSharingIntent from 'react-native-receive-sharing-intent';

import Navigation from './src/navigation';
import { Amplify } from 'aws-amplify';
import awsConfig from './src/aws-exports';
import AuthProvider from './src/contexts/Auth/Auth';
import { ErrorProvider } from './src/contexts/ErrorContext';
import { useTheme } from './src/theme/colors';
import { registerForPushNotificationsAsync } from './src/utils/notifications';
import {WEB_MAXWIDTH} from "./constants";
import {IsMobile} from "./src/theme";
import NativeAppBanner from './src/components/NativeAppBanner';


// 👇 Add this useEffect before return()

Amplify.configure(awsConfig);

// Foreground behavior for notifications
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: false,
  }),
});

async function lockPortrait() {
  try {
    await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT);
  } catch (e) {
    console.warn('Failed to lock orientation:', e);
  }
}

export interface ImageReceived {
  contentUri: string;
  fileName: string;
  mimeType: string;
  filePath: string;
}

export default function App() {
  const theme = useTheme();
  const navRef = useNavigationContainerRef();

  const [file, setFile] = useState<ImageReceived | null>(null);

  // Refs to hold subscription objects so we can remove them
  const notificationListener = useRef<Notifications.Subscription | null>(null);
  const responseListener = useRef<Notifications.Subscription | null>(null);
  const [initialRoute, setInitialRoute] = useState<string | undefined>(undefined);
  const [initialParams, setInitialParams] = useState<Record<string, any>>({});

  // Push notifications bootstrap
  useEffect(() => {
    (async () => {
      const token = await registerForPushNotificationsAsync();
      if (token) {
        // TODO: send token to your Spring Boot backend and associate with the authenticated user
        // await api.registerDeviceToken({ token });
      }
    })();

    // Foreground notifications listener
    notificationListener.current = Notifications.addNotificationReceivedListener((notification) => {
      console.log('Notification received (foreground):', notification);
      // If you prefer custom in-app banners, you can also show your own UI here.
    });

    // When the user taps a notification (background/quit -> foreground)
    responseListener.current = Notifications.addNotificationResponseReceivedListener((response) => {
      try {
        const data = response.notification.request.content.data as any;
        const route = data?.route ?? 'Chat';
        if (navRef.isReady()) {
          // Pass through any payload params you added on the server
          navRef.navigate(route as never, data as never);
        }
      } catch (e) {
        console.warn('Failed to handle notification response:', e);
      }
    });

    return () => {
      if (notificationListener.current) {
        Notifications.removeNotificationSubscription(notificationListener.current);
        notificationListener.current = null;
      }
      if (responseListener.current) {
        Notifications.removeNotificationSubscription(responseListener.current);
        responseListener.current = null;
      }
    };
  }, [navRef]);

  // ReceiveSharingIntent (share sheet -> your app)
  useEffect(() => {
    if (Platform.OS !== 'web' && ReceiveSharingIntent) {
      ReceiveSharingIntent.getReceivedFiles(
        (files: any[]) => {
          if (files?.length) {
            const f = files[0] as ImageReceived;
            setFile(f);
          }
        },
        (error: any) => {
          console.warn('ReceiveSharingIntent error:', error);
        },
        Platform.OS === 'ios' ? 'services.umo.OneSpace' : 'com.theonespace.OneSpace'
      );

      return () => {
        // Important: cleans pending files & listeners
        ReceiveSharingIntent.clearReceivedFiles();
      };
    }
  }, []);

  // Orientation lock
  useEffect(() => {
    lockPortrait();
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web') {
      try {
        const params = new URLSearchParams(window.location.search);
        const username = params.get('username') || params.get('email');
        const password = params.get('password') || params.get('pwd');
        if (username) {
          setInitialRoute('LoginRoute');
          setInitialParams({ username, password });
        }
      } catch (err) {
        console.warn('Failed to parse initial web URL params:', err);
      }
    }
  }, []);

  return (
    <SafeAreaProvider>
      <SafeAreaView
        style={{ flex: 1, backgroundColor: theme.BACKGROUND }}
        edges={['top', 'bottom', 'right', 'left']}
      >
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.select({ ios: undefined, android: 'height' })}
          enabled={false} // keep your current behavior; set true if you want it active
        >
          <PaperProvider
            theme={{
              // Keep your theme mapping; add more if needed
              colors: {
                onSurface: theme.TEXT,
              },
            }}
          >
            <AuthProvider>
              <ErrorProvider>
                <View
                  style={{
                    flex: 1,
                    alignItems: 'center',
                    backgroundColor: theme.BACKGROUND,
                  }}
                >
                  <View
                    style={{
                      flex: 1,
                      width: IsMobile ?  '100%' : WEB_MAXWIDTH,
                      maxWidth: IsMobile ? '100%' : WEB_MAXWIDTH,
                      borderWidth: IsMobile ? 0 : 1, borderStyle: "dashed", borderColor: "lightgrey"
                    }}
                  >
                    <NativeAppBanner />
                    <NavigationContainer ref={navRef}>
                      <Navigation sharedImage={file} initialRoute={initialRoute} initialParams={initialParams} />
                    </NavigationContainer>
                  </View>
                </View>
              </ErrorProvider>
            </AuthProvider>
          </PaperProvider>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </SafeAreaProvider>
  );
}